import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { errorSnackBar } from "../actions/common.action";
import { saveAccountingConnectionSettings } from "../slice/accounting.slice";

export function useAccountingForm(selectedConnection, accountsList, taxRatesList, currenciesList, loadingMetadata, onSaveSuccess, itemsList = []) {
  const dispatch = useDispatch();

  const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
  const [taxType, setTaxType] = useState("GST on Income");
  const [accountCode, setAccountCode] = useState("200");
  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState("DRAFT");
  const [expenseAccountCode, setExpenseAccountCode] = useState("400");
  const [cogsAccountCode, setCogsAccountCode] = useState("400");
  const [contractorAccountCode, setContractorAccountCode] = useState("400");
  const [cogsAccountType, setCogsAccountType] = useState("EXPENSE");
  const [contractorAccountType, setContractorAccountType] = useState("EXPENSE");
  const [defaultCurrency, setDefaultCurrency] = useState("AUD");
  const [expenseTaxType, setExpenseTaxType] = useState("GST on Expenses");
  const [trackingCategoryName, setTrackingCategoryName] = useState("");
  const [squashLinesInvoices, setSquashLinesInvoices] = useState(false);
  const [squashLinesBills, setSquashLinesBills] = useState(false);
  const [squashRetention, setSquashRetention] = useState(false);
  const [lineAmountTypes, setLineAmountTypes] = useState("Exclusive");
  const [defaultItemId, setDefaultItemId] = useState("");

  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef(null);

  const selectedConnectionId = selectedConnection?.id;

  // Track dirty changes
  useEffect(() => {
    if (!selectedConnectionId || !initialValuesRef.current) {
      setIsDirty(false);
      return;
    }
    const hasChanges =
      invoiceStatus !== initialValuesRef.current.invoiceStatus ||
      taxType !== initialValuesRef.current.taxType ||
      accountCode !== initialValuesRef.current.accountCode ||
      purchaseOrderStatus !== initialValuesRef.current.purchaseOrderStatus ||
      expenseAccountCode !== initialValuesRef.current.expenseAccountCode ||
      cogsAccountCode !== initialValuesRef.current.cogsAccountCode ||
      contractorAccountCode !== initialValuesRef.current.contractorAccountCode ||
      defaultCurrency !== initialValuesRef.current.defaultCurrency ||
      expenseTaxType !== initialValuesRef.current.expenseTaxType ||
      trackingCategoryName !== initialValuesRef.current.trackingCategoryName ||
      squashLinesInvoices !== initialValuesRef.current.squashLinesInvoices ||
      squashLinesBills !== initialValuesRef.current.squashLinesBills ||
      squashRetention !== initialValuesRef.current.squashRetention ||
      lineAmountTypes !== initialValuesRef.current.lineAmountTypes ||
      defaultItemId !== initialValuesRef.current.defaultItemId ||
      cogsAccountType !== initialValuesRef.current.cogsAccountType ||
      contractorAccountType !== initialValuesRef.current.contractorAccountType;

    setIsDirty(hasChanges);
  }, [
    invoiceStatus, taxType, accountCode,
    purchaseOrderStatus, expenseAccountCode, cogsAccountCode, contractorAccountCode,
    defaultCurrency, expenseTaxType, trackingCategoryName,
    squashLinesInvoices, squashLinesBills, squashRetention, lineAmountTypes,
    defaultItemId,
    cogsAccountType, contractorAccountType, selectedConnectionId
  ]);

  const selectConnection = useCallback((connection) => {
    const settings = connection.settings || {};

    // Clear initialValuesRef temporarily during settings population to prevent false warnings
    initialValuesRef.current = null;

    setInvoiceStatus(settings.invoiceStatus || "DRAFT");
    setTaxType(settings.taxType || "GST on Income");
    setAccountCode(settings.accountCode || "200");
    setPurchaseOrderStatus(settings.purchaseOrderStatus || "DRAFT");
    setExpenseAccountCode(settings.expenseAccountCode || "400");
    setCogsAccountCode(settings.cogsAccountCode || "");
    setContractorAccountCode(settings.contractorAccountCode || "");
    setCogsAccountType(settings.cogsAccountType || "EXPENSE");
    setContractorAccountType(settings.contractorAccountType || "EXPENSE");
    setDefaultCurrency((settings.defaultCurrency === undefined || settings.defaultCurrency === null) ? "AUD" : (settings.defaultCurrency === "" ? "none" : settings.defaultCurrency));
    setExpenseTaxType(settings.expenseTaxType || "GST on Expenses");
    setTrackingCategoryName(settings.trackingCategoryName === "" ? "none" : (settings.trackingCategoryName || ""));
    const loadedSquashInvoices = settings.squashLinesInvoices !== undefined ? settings.squashLinesInvoices : (settings.squashLineItems !== undefined ? settings.squashLineItems : true);
    const loadedSquashBills = settings.squashLinesBills !== undefined ? settings.squashLinesBills : (settings.squashLineItems === true);
    setSquashLinesInvoices(loadedSquashInvoices);
    setSquashLinesBills(loadedSquashBills);
    setSquashRetention(settings.squashRetention || false);
    setLineAmountTypes(settings.lineAmountTypes || "Exclusive");
    setDefaultItemId(settings.defaultItemId || "1");

    // Setup initial loaded values
    const initialSnapshot = {
      invoiceStatus: settings.invoiceStatus || "DRAFT",
      taxType: settings.taxType || "GST on Income",
      accountCode: settings.accountCode || "200",
      purchaseOrderStatus: settings.purchaseOrderStatus || "DRAFT",
      expenseAccountCode: settings.expenseAccountCode || "400",
      cogsAccountCode: settings.cogsAccountCode || "",
      contractorAccountCode: settings.contractorAccountCode || "",
      cogsAccountType: settings.cogsAccountType || "EXPENSE",
      contractorAccountType: settings.contractorAccountType || "EXPENSE",
      defaultCurrency: (settings.defaultCurrency === undefined || settings.defaultCurrency === null) ? "AUD" : (settings.defaultCurrency === "" ? "none" : settings.defaultCurrency),
      expenseTaxType: settings.expenseTaxType || "GST on Expenses",
      trackingCategoryName: settings.trackingCategoryName === "" ? "none" : (settings.trackingCategoryName || ""),
      squashLinesInvoices: loadedSquashInvoices,
      squashLinesBills: loadedSquashBills,
      squashRetention: settings.squashRetention || false,
      lineAmountTypes: settings.lineAmountTypes || "Exclusive",
      defaultItemId: settings.defaultItemId || "1"
    };
    initialValuesRef.current = initialSnapshot;
  }, []);

  // Track active connection ID to prevent re-initializing snapshot on form edits
  const activeConnIdRef = useRef(null);

  // Auto-initialize form snapshot only when a different connection is selected (id changes).
  // Do NOT re-init on selectedConnection object reference changes (e.g., post-save fetchConnections),
  // as that would reset cogsAccountCode/contractorAccountCode if API returns them empty.
  useEffect(() => {
    if (selectedConnection) {
      if (selectedConnection.id !== activeConnIdRef.current) {
        activeConnIdRef.current = selectedConnection.id;
        selectConnection(selectedConnection);
      }
    }
  }, [selectedConnection?.id, selectConnection]);

  // Reactive default settings population from loaded metadata lists.
  // IMPORTANT: Check current state values (cogsAccountCode, contractorAccountCode, accountCode)
  // instead of settings object to avoid stale closure bug — selectedConnection is not in deps
  // to prevent infinite re-render loops, so settings read from it would be stale.
  useEffect(() => {
    if (!selectedConnectionId || loadingMetadata) return;

    if (accountsList.length > 0) {
      // Only auto-select revenue account if current state value is empty
      if (!accountCode && !isDirty) {
        const salesAcc = accountsList.find(a => {
          const name = (a.name || '').toLowerCase();
          const type = (a.type || '').toLowerCase();
          return name.includes('sales') || name.includes('revenue') || type.includes('revenue') || type.includes('sales');
        });
        if (salesAcc) {
          setAccountCode(salesAcc.code);
        }
      }
    }

    if (taxRatesList.length > 0) {
      setTaxType(prev => {
        if (!prev || prev === 'GST on Income' || prev === 'GST') {
          const exemptRate = taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt'));
          return exemptRate ? (exemptRate.code || exemptRate.id || exemptRate.name) : taxRatesList[0].code;
        }
        return prev;
      });
      setExpenseTaxType(prev => {
        if (!prev || prev === 'GST on Expenses' || prev === 'GST') {
          const exemptRate = taxRatesList.find(t => {
            const nameLower = String(t.name || t.code).toLowerCase();
            return (nameLower.includes('tax exempt') || nameLower.includes('exempt')) && !nameLower.includes('sales');
          }) || taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt'));
          return exemptRate ? (exemptRate.code || exemptRate.id || exemptRate.name) : taxRatesList[0].code;
        }
        return prev;
      });
    }

    if (currenciesList.length > 0) {
      setDefaultCurrency(prev => {
        const isValid = currenciesList.some(c => c.code === prev);
        if (!isValid || prev === 'none' || prev === '') {
          return currenciesList[0].code;
        }
        return prev;
      });
    }

    if (itemsList && itemsList.length > 0) {
      setDefaultItemId(prev => {
        const isValid = itemsList.some(i => i.code === prev);
        if (!isValid || prev === '' || prev === undefined) {
          const defaultItemOne = itemsList.find(i => String(i.code) === '1');
          return defaultItemOne ? defaultItemOne.code : itemsList[0].code;
        }
        return prev;
      });
    }
  }, [accountsList, taxRatesList, currenciesList, itemsList, selectedConnectionId, loadingMetadata,
      isDirty, accountCode, cogsAccountCode, contractorAccountCode]);



  const saveSettings = async () => {
    if (!selectedConnectionId) return false;

    if (!cogsAccountCode) {
      dispatch(errorSnackBar("Please select a Supplier Account (COGS)"));
      return false;
    }
    if (!contractorAccountCode) {
      dispatch(errorSnackBar("Please select a Contractor Account (Subcontractors)"));
      return false;
    }

    const payload = {
      invoiceStatus,
      taxType,
      accountCode,
      purchaseOrderStatus,
      expenseAccountCode,
      cogsAccountCode,
      contractorAccountCode,
      cogsAccountType,
      contractorAccountType,
      defaultCurrency: defaultCurrency === "none" ? "" : defaultCurrency,
      expenseTaxType,
      trackingCategoryName: trackingCategoryName === "none" ? "" : trackingCategoryName,
      squashLinesInvoices,
      squashLinesBills,
      squashRetention,
      lineAmountTypes,
      defaultItemId
    };

    try {
      await dispatch(saveAccountingConnectionSettings(selectedConnectionId, payload));

      if (onSaveSuccess) {
        onSaveSuccess(payload);
      }

      // Update snapshot with the newly saved values
      initialValuesRef.current = {
        invoiceStatus,
        taxType,
        accountCode,
        purchaseOrderStatus,
        expenseAccountCode,
        cogsAccountCode,
        contractorAccountCode,
        cogsAccountType,
        contractorAccountType,
        defaultCurrency,
        expenseTaxType,
        trackingCategoryName,
        squashLinesInvoices,
        squashLinesBills,
        squashRetention,
        lineAmountTypes,
        defaultItemId
      };

      setIsDirty(false);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      await saveSettings();
    } catch (err) {
      const isSyncLock = err?.response?.data?.errorCode === 'SYNC_LOCK' || err?.response?.status === 423;
      if (isSyncLock) {
        dispatch(errorSnackBar('This connection is currently syncing. Settings are read-only until the sync completes.'));
      } else {
        dispatch(errorSnackBar(err.message || 'Failed to update settings'));
      }
    }
  };

  return {
    invoiceStatus, setInvoiceStatus,
    taxType, setTaxType,
    accountCode, setAccountCode,
    purchaseOrderStatus, setPurchaseOrderStatus,
    expenseAccountCode, setExpenseAccountCode,
    cogsAccountCode, setCogsAccountCode,
    contractorAccountCode, setContractorAccountCode,
    cogsAccountType, setCogsAccountType,
    contractorAccountType, setContractorAccountType,
    defaultCurrency, setDefaultCurrency,
    expenseTaxType, setExpenseTaxType,
    trackingCategoryName, setTrackingCategoryName,
    squashLinesInvoices, setSquashLinesInvoices,
    squashLinesBills, setSquashLinesBills,
    squashRetention, setSquashRetention,
    lineAmountTypes, setLineAmountTypes,
    defaultItemId, setDefaultItemId,
    isDirty, setIsDirty,
    selectConnection,
    saveSettings,
    handleSaveSettings
  };
}
